/**
 * DAILY OUTPUT · DATA CHECK — Jay via Max, 2026-10-18, from the DAC productivity board's "Kiểm tra dữ
 * liệu"; Max: "làm theo đề xuất tốt nhất nhé". The server (outputCheck.js) finds what cannot be
 * measured with and the AI writes one sentence on what to fix first; this panel shows both, a grid of
 * the last fourteen days, and takes the reader to the day with one click.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, TriangleAlert, ShieldCheck } from 'lucide-react';
import AiTag from './AiTag';
import './OutputCheck.css';

const ddmm = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/* Keys of xOutCheck.wd, by getUTCDay(). */
const WEEKDAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const n0 = (v) => Math.round(Number(v) || 0).toLocaleString('en-GB');
const minsSecs = (t, s) => (s % 60
  ? t('xOutCheck.perPiece', { m: Math.floor(s / 60), s: s % 60 })
  : t('xOutCheck.perPieceMin', { m: Math.floor(s / 60) }));

/* The level and the rule are the server's keys; only their words are translated here. */
const LEVEL = (t) => ({ unreliable: t('xOutCheck.levelUnreliable'), check: t('xOutCheck.levelCheck'), info: t('xOutCheck.levelInfo') });
/* Each finding in a sentence, worded by the screen from the server's figures. */
const HEAD = (t) => ({
  no_time: (f) => t('xOutCheck.headNoTime', { count: f.count }),
  long_day: (f) => t('xOutCheck.headLongDay', { count: f.count, max: f.maxLineHours }),
  even_split: (f) => t('xOutCheck.headEvenSplit', { count: f.count }),
  unmatched: (f) => t('xOutCheck.headUnmatched', { count: f.count }),
  no_sheet: (f) => t('xOutCheck.headNoSheet', { count: f.count }),
});
/* `missing` comes from the server as 'workers', 'hours' or 'workers and hours'. */
const missingText = (t, m) => ({
  workers: t('xOutCheck.noWorkers'),
  hours: t('xOutCheck.noHours'),
  'workers and hours': t('xOutCheck.noWorkersHours'),
}[m] || t('xOutCheck.noWhat', { what: m }));
const LINE = (t) => ({
  no_time: (r) => `${r.line ? `${r.line} · ` : ''}${r.item} (${r.part}) · ${t('xOutCheck.pcs', { n: n0(r.pieces) })} · ${missingText(t, r.missing)}`,
  long_day: (r) => `${r.line}: ${r.hours} h · ${r.items.join(', ')}`,
  even_split: (r) => `${r.line}: ${minsSecs(t, r.secondsPerPiece)} · ${r.items.join(', ')}`,
  unmatched: (r) => t('xOutCheck.rowsPcs', { count: r.rows, pcs: n0(r.pieces) }),
  no_sheet: () => t('xOutCheck.noSheet'),
});
const CELL = { ok: '✓', bad: '!', none: '–', later: '…', off: '' };
const CELL_TITLE = (t) => ({
  ok: t('xOutCheck.cellOk'), bad: t('xOutCheck.cellBad'), none: t('xOutCheck.cellNone'),
  later: t('xOutCheck.cellLater'), off: t('xOutCheck.sunday'),
});
const GRID_ROWS = (t) => [['output', t('xOutCheck.rowOutput')], ['workers', t('xOutCheck.rowWorkers')],
  ['hours', t('xOutCheck.rowHours')], ['matched', t('xOutCheck.rowMatched')]];
const SHOW = 6;

export default function OutputCheck({ api, vendor, onPickDay }) {
  const { t } = useTranslation();
  const level = LEVEL(t);
  const head = HEAD(t);
  const lineOf = LINE(t);
  const cellTitle = CELL_TITLE(t);
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState({});

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${api}/check?${new URLSearchParams({ vendor, days })}`, { credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setD(j); setMore({});
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [api, vendor, days]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="oc">
      <div className="oc-head">
        <ShieldCheck size={14} />
        <strong>{t('xOutCheck.title')}</strong>
        {d && <em>{t('xOutCheck.meta', { vendor, from: ddmm(d.from), to: ddmm(d.to), rows: n0(d.rows), last: d.lastSheet ? ddmm(d.lastSheet) : '—' })}</em>}
        <span className="oc-spans" role="group" aria-label={t('xOutCheck.period')}>
          {[14, 30, 90].map((n) => (
            <button key={n} type="button" className={`sp-btn tiny ${days === n ? 'cap-on' : ''}`} aria-pressed={days === n}
              onClick={() => setDays(n)}>{t('xOutCheck.nDays', { n })}</button>
          ))}
        </span>
        <button type="button" className="sp-btn tiny" onClick={load} disabled={busy} title={t('xOutCheck.checkAgain')}><RefreshCw size={12} /></button>
      </div>
      {err && <div className="sp-error">{err}</div>}
      {busy && !d && <div className="co-pick-empty">{t('xOutCheck.checking')}</div>}
      {d && (
        <>
          <div className={`oc-ai${d.findings.length ? '' : ' clean'}`}>
            <AiTag title={t('xOutCheck.aiTitle')} />{' '}
            {d.findings.length
              ? (d.summary || <span className="oc-dim">{d.aiError ? t('xOutCheck.aiDidNotRun', { err: d.aiError }) : t('xOutCheck.noSummary')}</span>)
              : t('xOutCheck.nothingToFix')}
          </div>
          {d.findings.map((f) => {
            const open = !!more[f.rule];
            const list = open ? f.rows : f.rows.slice(0, SHOW);
            return (
              <div key={f.rule} className={`oc-find ${f.level}`}>
                <div className="oc-find-head">
                  <span className={`oc-level ${f.level}`}>{level[f.level]}</span>
                  <span>{head[f.rule](f)}</span>
                </div>
                <div className="oc-find-rows">
                  {list.map((r, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <button key={i} type="button" className="oc-day" onClick={() => onPickDay(r.day)} title={t('xOutCheck.openDay')}>
                      <b>{ddmm(r.day)}</b> {lineOf[f.rule](r)}
                    </button>
                  ))}
                  {f.rows.length > SHOW && (
                    <button type="button" className="oc-more" onClick={() => setMore((m) => ({ ...m, [f.rule]: !open }))}>
                      {open ? t('xOutCheck.showFewer') : t('xOutCheck.more', { n: f.rows.length - SHOW })}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <div className="oc-grid-wrap">
            <table className="oc-grid">
              <thead>
                <tr>
                  <th aria-label={t('xOutCheck.checkCol')} />
                  {d.grid.map((g) => (
                    <th key={g.day}>
                      <button type="button" className="oc-gday" onClick={() => onPickDay(g.day)} title={t('xOutCheck.openDay')}>
                        <span>{t(`xOutCheck.wd.${WEEKDAY[new Date(`${g.day}T00:00:00Z`).getUTCDay()]}`)}</span>{ddmm(g.day)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {GRID_ROWS(t).map(([k, label]) => (
                  <tr key={k}>
                    <th>{label}</th>
                    {d.grid.map((g) => <td key={g.day} className={`oc-c ${g[k]}`} title={cellTitle[g[k]]}>{CELL[g[k]]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="oc-legend">
              <span className="oc-c ok">✓</span> {t('xOutCheck.legendOk')}
              <span className="oc-c bad">!</span> {t('xOutCheck.legendBad')}
              <span className="oc-c none">–</span> {t('xOutCheck.legendNone')}
              <span className="oc-c later">…</span> {t('xOutCheck.legendLater', { last: d.lastSheet ? ddmm(d.lastSheet) : '—' })}
              <span className="oc-c off" /> {t('xOutCheck.sunday')}
            </div>
          </div>
          {!d.findings.length && <div className="oc-dim"><TriangleAlert size={12} /> {t('xOutCheck.nothingFound', { n: d.days })}</div>}
        </>
      )}
    </div>
  );
}
